import { useEffect, useMemo, useState } from "react";
import { useRecordContext } from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { callApi } from "@/dataprovider/misc-apis";
import { formatDisplayDateTime } from "@/utils/date-format";

type EventRsvpRow = {
  id: string;
  hofItsNo: string;
  hofName: string | null;
  markaz: string;
  familyAttendingIds: string[];
  submittedAt: string;
};

type EventRsvpsResponse = {
  count: number;
  rows: EventRsvpRow[];
};

type EventRsvpAttendeesResponse = {
  count: number;
  rows: Array<{
    id: string;
    attendeeItsNo: string;
    hofItsNo: string;
    submittedAt: string;
  }>;
};

function StatBlock({ value, label }: { value: number; label: string }) {
  return (
    <Grid size={{ xs: 6, sm: 4 }}>
      <Typography variant="h5" fontWeight={600} component="p" sx={{ lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
        {label}
      </Typography>
    </Grid>
  );
}

export default function EventRsvpSection() {
  const record = useRecordContext<{ id?: string; rsvpEnabled?: boolean }>();
  const [rsvps, setRsvps] = useState<EventRsvpsResponse | null>(null);
  const [attendees, setAttendees] = useState<EventRsvpAttendeesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = record?.id;
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    void Promise.all([
      callApi({ location: "events", method: "GET", id: `${String(id)}/rsvps` }),
      callApi({ location: "events", method: "GET", id: `${String(id)}/rsvp-attendees` }),
    ])
      .then(([rsvpRes, attendeeRes]) => {
        if (cancelled) return;
        setRsvps((rsvpRes.data ?? null) as EventRsvpsResponse | null);
        setAttendees((attendeeRes.data ?? null) as EventRsvpAttendeesResponse | null);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "Failed to load RSVP data");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [record?.id]);

  const rows = rsvps?.rows ?? [];
  const totalAttendees = attendees?.count ?? 0;
  const familiesResponded = rsvps?.count ?? 0;

  const avgAttendeesPerFamily = useMemo(() => {
    if (familiesResponded <= 0) return 0;
    return Math.round((totalAttendees / familiesResponded) * 10) / 10;
  }, [familiesResponded, totalAttendees]);

  if (!record?.id) {
    return null;
  }

  return (
    <Card variant="outlined" elevation={0} sx={{ borderRadius: 1 }}>
      <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            WhatsApp RSVP
          </Typography>
          <Chip
            size="small"
            label={record.rsvpEnabled === false ? "Closed" : "Open"}
            color={record.rsvpEnabled === false ? "default" : "success"}
            variant="outlined"
          />
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Families who submitted via the Event RSVP WhatsApp Flow. Attendee counts include HOF and
          family members they selected.
        </Typography>

        {loading && <CircularProgress size={22} sx={{ my: 1 }} />}
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}

        {!loading && !error && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <StatBlock value={familiesResponded} label="Families responded" />
              <StatBlock value={totalAttendees} label="Total attendees" />
              <StatBlock value={avgAttendeesPerFamily} label="Avg per family" />
            </Grid>

            {rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No RSVPs submitted yet.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 480 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>HOF name</TableCell>
                      <TableCell>HOF ITS</TableCell>
                      <TableCell>Markaz</TableCell>
                      <TableCell align="right">Attending</TableCell>
                      <TableCell>Member ITS</TableCell>
                      <TableCell>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.hofName?.trim() || "—"}</TableCell>
                        <TableCell>{row.hofItsNo || "—"}</TableCell>
                        <TableCell>{row.markaz?.trim() || "—"}</TableCell>
                        <TableCell align="right">{row.familyAttendingIds?.length ?? 0}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography variant="caption" component="span">
                            {(row.familyAttendingIds ?? []).join(", ") || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDisplayDateTime(row.submittedAt, { empty: "—" })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
